import type { Directive, DirectiveBinding } from 'vue'

interface ScrollObserverOptions {
  threshold?: number
  rootMargin?: string
  once?: boolean
  /** 触发动画方向：left | right | up */
  direction?: 'left' | 'right' | 'up'
}

interface ScrollObserverElement extends HTMLElement {
  _scrollObserverInstance?: IntersectionObserver
}

const defaultOptions: Required<ScrollObserverOptions> = {
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px',
  once: true,
  direction: 'up',
}

const scrollObserverDirective: Directive<
  ScrollObserverElement,
  ScrollObserverOptions | undefined
> = {
  mounted(
    el: ScrollObserverElement,
    binding: DirectiveBinding<ScrollObserverOptions | undefined>
  ) {
    const { threshold, rootMargin, once, direction } = {
      ...defaultOptions,
      ...binding.value,
    }

    el.classList.add('scroll-hidden')

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            el.classList.remove('scroll-hidden')
            el.classList.add(
              'scroll-visible',
              'animated',
              `animated${capitalize(direction)}`
            )
            if (once) {
              observer.unobserve(el)
            }
          } else if (!once) {
            el.classList.remove('scroll-visible')
            el.classList.add('scroll-hidden')
          }
        })
      },
      { threshold, rootMargin }
    )

    el._scrollObserverInstance = observer
    observer.observe(el)
  },

  unmounted(el: ScrollObserverElement) {
    if (el._scrollObserverInstance) {
      el._scrollObserverInstance.disconnect()
      delete el._scrollObserverInstance
    }
  },
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export default scrollObserverDirective
